import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import cx from 'classnames';
import FormContainer from '../../common/component/FormContainer';
import Nav from '../../common/component/Nav/Nav';
import MainContainer from '../../common/component/MainContainer';
import Footer from '../../common/component/Footer';
import { isLoggedIn } from '../../../engage/utils/auth-util';
import styles from './login-page.module.scss';

import '../../../engage/styles/engage.scss';
import { CustomInput } from '../../../engage/components/Input/Input';
import UserAPI from '../../apis/UserAPI';
import AuthorizationService from '../../../engage/service/AuthorizationService';
import FirebaseFeature from '../../../engage/utils/firebase';
import CmsAPI from '../../../common/apis/CmsAPI';
import { CustomButton } from '../../../engage/components/Button/Button';
import RadioButton from '../../../engage/components/RadioButton/RadioButton';
import MobileHeader from '../../../engage/components/MobileHeader';
import ProtectedPageSidebar from '../../../engage/components/Sidebar/ProtectedPageSidebar';
import { withTranslation } from 'react-i18next';
import { ToastNotification } from '../../../engage/components/Toast/Toast';
import { isEmailFormatValid } from '../../../engage/utils/stringHelper';
import VideoService from '../../../engage/service/VideoService';
import { sanitizeReturnUrl } from '../../common/util/returnUrl';

const navigateLoggedInUser = async props => {
  const id = localStorage.getItem('typebot-id');
  if (id) {
    try {
      const { id: storyId } = await VideoService.createFromTemporaryStory(id);
      localStorage.removeItem('typebot-id');
      props.history.push(`/eg/edit/${storyId}`);
    } catch (e) {
      console.error(e);
      props.history.push('/eg/home');
    }
  } else {
    const returnUrl = new URLSearchParams(props.location.search).get('returnUrl');
    const safeReturnUrl = sanitizeReturnUrl(returnUrl);
    if (safeReturnUrl) {
      props.history.push(safeReturnUrl);
    } else {
      props.history.push('/eg/home');
    }
  }
};

class LoginPage extends Component {
  constructor() {
    super();
    this.state = {
      username: '',
      password: '',
      loginError: false,
      loading: false,
      errorString: '',
      disabled: true,
      isEmailChecked: false,
      isRememberChecked: true,
      isUserChecking: true,
      isMobileView: false,
      webAccessError: false,
      isEmailValid: false,
      isEmailTouched: false,
    };

    this.formRef = React.createRef();
  }

  async componentDidMount() {
    /*
     * We cannot fully implement the registration of a new user in the desktop application,
     * since when creating a new account, confirmations are sent to the email
     * and the links in them will still lead to the web application.
     * Therefore, if the user in the desktop application entered a login that does not exist,
     * then he will be redirected here for a second attempt where he can register
     *
     * If there is a search param "desktopEmail",
     * the application will be able to understand that the user was redirected from the desktop application.
     *
     * But in this case, the old account must be logged out so that the new one can register,
     * so we are forced to log out the user in case he was logged in
     */
    const searchWithPlusSign = this.props.location.search.replaceAll('+', '%2B');
    const { desktopEmail } = queryString.parse(searchWithPlusSign);

    const firstUserCheck = await isLoggedIn();

    if (desktopEmail && firstUserCheck) {
      await AuthorizationService.logOut(desktopEmail);
    }

    const isUserLoggedIn = await isLoggedIn();

    if (isUserLoggedIn) {
      // If user already logged in, redirect them to the dashboard page
      await navigateLoggedInUser(this.props);
    } else {
      const mediaMatch = window.matchMedia('(max-width: 900px)');
      this.setState({
        isUserChecking: false,
        isMobileView: mediaMatch.matches,
        webAccessError: this.props.location.state?.web_access_error,
      });

      if (this.props.location.state?.email) {
        this.setState({
          username: this.props.location.state.email,
          isEmailValid: isEmailFormatValid(this.props.location.state.email),
          isEmailTouched: true,
        });
      }

      if (desktopEmail) {
        this.setState({ username: desktopEmail });
      }

      const { t } = this.props;

      this.props.location.state?.passwordResetSucceed &&
        ToastNotification({
          message: t('login.password_reset_successfully'),
          iconName: 'success',
          isMobileView: mediaMatch.matches,
        });
    }

    const qs = queryString.parse(this.props.location.search);
    if ('error' in qs) {
      let errorString = 'An unknown error occurred';
      if (qs.error === 'badcredentials') {
        errorString = 'Invalid username or password';
      } else if (qs.error === 'notactive') {
        errorString = 'Account is not active. Contact support@vormats.com';
      } else if (qs.error === 'locked') {
        errorString = 'Account locked. Contact support@vormats.com';
      } else if (qs.error === 'notverified') {
        errorString = 'Account was not verified in time. Check you trash for our verification email, or contact support@vormats.com';
      } else if (qs.error === 'expired') {
        errorString = 'Your account has expired. Please contact support@vormats.com';
      } else if (qs.error === 'trialexpired') {
        errorString = 'Your free trial has expired, please contact support@vormats.com';
      }
      this.setState({ loginError: true, errorString });
    }

    const authResponse = queryString.parse(this.props.location.search);
    const authResponseHash = queryString.parse(this.props.location.hash);
    if ('code' in authResponse) {
      this.setState({ loading: true });

      // We're dealing with a redirect after a successful SSO login (code grant flow)
      let ssoSuccess = await AuthorizationService.exchangeAuthCodeForTokens(authResponse);

      if (ssoSuccess) {
        await navigateLoggedInUser(this.props); // Redirect to Dashboard after successful login
      }
    } else if ('id_token' in authResponseHash) {
      this.setState({ loading: true });

      // We're dealing with a redirect after a successful SSO login (implicit grant flow)
      let ssoSuccess = await AuthorizationService.finishSSOLogin(authResponseHash);

      if (ssoSuccess) {
        await navigateLoggedInUser(this.props); // Redirect to Dashboard after successful login
      }
    } else if (
      ('error_description' in authResponseHash && authResponseHash['error_description'].includes('Already found an entry')) ||
      ('error' in authResponse && authResponse.error.includes('Already found an entry'))
    ) {
      // An "Already found an entry for username" error has been thrown,
      // fire the initial federatedSignIn once more to work around this
      // @see: https://bobbyhadz.com/blog/aws-amplify-already-found-entry
      // @see: https://stackoverflow.com/questions/47815161/cognito-auth-flow-fails-with-already-found-an-entry-for-username-facebook-10155

      console.log('Already found an entry error, fire SSO login again');

      // Get the awsConfig from localStorage
      let awsConfig = JSON.parse(localStorage.getItem('awsConfig'));

      console.log('awsConfig', awsConfig);

      // Perform the SSO login step again to work around the already found error
      if (awsConfig !== null) {
        await AuthorizationService.sso(awsConfig);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.username !== this.state.username ||
      prevState.password !== this.state.password ||
      this.state.isEmailChecked !== prevState.isEmailChecked
    ) {
      this.setState({
        disabled: this.state.isEmailChecked
          ? !(this.state.username?.length > 0 && this.state.password?.length > 0)
          : !(this.state.username?.length > 0),
      });
    }
  }

  getEmailFieldError = () => {
    const { isEmailTouched, isEmailChecked, isEmailValid, webAccessError } = this.state;
    if (isEmailTouched && !isEmailValid) {
      return this.props.t('my_video.end_video.cta.invalid_email');
    }
    if (webAccessError) {
      return (
        <span>
          {this.props.t('log_in.product_not_included.part_one')} {<a href="mailto:support@vormats.com">Support Team</a>}{' '}
          {this.props.t('log_in.product_not_included.part_two')}
        </span>
      );
    }
    if (isEmailChecked) {
      return this.props.t('log_in.incorrect_email_or_password');
    }
    return '';
  };

  onLoginFormChange = e => {
    const name = e.target.name;
    const value = name === 'username' ? e.target.value.toLowerCase() : e.target.value;
    const updatedSate = { [name]: value, loginError: false };
    if (name === 'username') {
      updatedSate.isEmailValid = isEmailFormatValid(value);
    }
    this.setState({ ...updatedSate });
  };

  onEmailInputBlur = () => {
    this.setState({ isEmailTouched: true });
  };

  onForgotClick = () => {
    this.props.history.push({ pathname: '/forgot-password', state: { email: this.state.username } });
  };

  onSubmit = async e => {
    e.preventDefault();
    this.setState({ loading: true });

    if (this.state.isEmailChecked) {
      try {
        // Use Axios to perform POST to /login to retrieve UserPoolInfo
        const { user, response } = await AuthorizationService.login(this.state.username, this.state.password);
        const appConfig = await CmsAPI.getAppConfig(user.username);
        const appConfigFeatures = appConfig.result.features;
        const featureMFA = appConfigFeatures.find(feature => feature.name === 'mfa_software_token');
        const featureMFAIsEnabled = featureMFA.enabled;
        const featureWebAccess = appConfigFeatures.find(feature => feature.name === 'web_access');
        if (featureWebAccess) {
          if (!featureWebAccess.enabled) {
            this.setState({ loading: false, loginError: true, webAccessError: true });
            return;
          }
        }
        const featureFirebase = appConfigFeatures.find(feature => feature.name === 'firebase');
        const featureFirebaseIsEnabled = featureFirebase.enabled;
        if (featureFirebaseIsEnabled) FirebaseFeature.initializeFirebase();

        // If user unchecked "Remember me", set a session expiration time
        if (!this.state.isRememberChecked) {
          const hoursTillSessionExpires = 8;
          localStorage.setItem('sessionExpires', Date.now() + hoursTillSessionExpires * 60 * 60 * 1000);
        }

        // If user is signing in with a temporary password...
        if (user.signInUserSession == null && user.challengeName == 'NEW_PASSWORD_REQUIRED') {
          this.props.history.push({
            pathname: '/reset-password',
            search: '?challenge=new_password_required',
            state: JSON.stringify({ user, response, hasMFA: featureMFAIsEnabled }),
          });
        } else if (featureMFAIsEnabled === true) {
          if (user.challengeName === 'SOFTWARE_TOKEN_MFA' || user.preferredMFA === 'SOFTWARE_TOKEN_MFA') {
            // localStorage.removeItem('userDetails');
            this.props.history.push({
              pathname: '/OTP/verify',
              state: JSON.stringify({ user, username: this.state.username }),
              search: this.props.location.search,
            });
          } else if (user.preferredMFA === 'NOMFA') {
            this.props.history.push({
              pathname: '/OTP/setup',
              state: JSON.stringify({ user, response }),
              search: this.props.location.search,
            });
          }
        } else {
          // if not featured MFA
          localStorage.setItem(
            'userDetails',
            JSON.stringify({
              username: user.username,
              userPoolId: user.pool.userPoolId,
              appClientId: user.pool.clientId,
              identityPoolId: response.IdentityPoolId,
              userPoolWebClientId: response.UserPoolClientAppId,
              email: user.signInUserSession == null ? user.challengeParam.userAttributes.email : user.attributes.email,
              name: user.signInUserSession == null ? user.challengeParam.userAttributes.name : user.attributes.name,
              companyId: response.companyId,
              companyName: response.companyName,
              region: response.Region,
              bucket: response.InputBucketName,
              ssoDomain: response.SSODomain,
            })
          );
          await navigateLoggedInUser(this.props); // Redirect to Dashboard after successful login
        }
        this.setState({ loading: false });
      } catch (err) {
        if (err.message.includes('User is disabled.')) {
          const { t } = this.props;

          ToastNotification({
            message: t('login.user_disabled'),
            isMobileView: this.state.isMobileView,
          });
        }
        this.setState({ loading: false, loginError: true });
      }
    } else {
      // If email not checked
      try {
        let domainRecognized = false;
        let awsConfig = await UserAPI.getAwsConfigForUsername(this.state.username);
        const isEmailRecognized = awsConfig;
        if (!isEmailRecognized) {
          const emailDomain = this.state.username.split('@').pop();
          awsConfig = await UserAPI.getAwsConfigForDomain(emailDomain);
          domainRecognized = await UserAPI.isDomainRecognized(awsConfig);
        }

        // Check if SSO is enabled
        const featureSSO = awsConfig.companyFeatures?.filter(feature => feature.name === 'sso')[0];

        if (featureSSO?.enabled) {
          // Trigger SSO login
          await AuthorizationService.sso(awsConfig);
        } else {
          this.setState({ isEmailChecked: true, loading: false });

          if (domainRecognized) {
            this.setState({ loading: false });
            console.log('Domain Recognized');
            this.props.history.push({ pathname: '/sign-up', state: { email: this.state.username } });
          }
        }
      } catch (e) {
        console.log('isAvailable error', e);
        if (e.includes('domain')) {
          this.setState({ isEmailChecked: true });
        }
        this.setState({ loading: false });
      }
    }
  };

  handleRememberChecked = e => {
    this.setState(prevState => ({ ...prevState, isRememberChecked: !prevState.isRememberChecked }));
  };

  render() {
    const { t } = this.props;

    const {
      loginError,
      disabled,
      isEmailChecked,
      isEmailTouched,
      isEmailValid,
      isRememberChecked,
      isUserChecking,
      isMobileView,
      webAccessError,
    } = this.state;
    if (isUserChecking) {
      return <div></div>;
    }

    return (
      this.props.isReady && (
        <>
          {!isMobileView && <Nav />}
          <MobileHeader libraryMode={false} />
          <MainContainer className={cx('content-padding play-bg', { [styles.clearBackground]: isMobileView })}>
            {isMobileView && <ProtectedPageSidebar />}
            <div>
              <div>
                <FormContainer
                  title={isMobileView ? t('log_in.title') : isEmailChecked ? t('log_in.title_welcome') : t('log_in.title')}
                  className={cx(styles.formHeader)}
                >
                  <div className={styles.formContainer}>
                    <form
                      action="/login"
                      method="POST"
                      ref={this.formRef}
                      onSubmit={this.onSubmit}
                      className={cx(styles.authCard, styles.login)}
                    >
                      <CustomInput
                        type="email"
                        name="username"
                        fieldName={t('general.email')}
                        placeholder={t('general.email')}
                        disabled={this.state.loading}
                        invalid={(!isEmailChecked && webAccessError) || (isEmailTouched && !isEmailValid)}
                        validateMessage={this.getEmailFieldError()}
                        value={this.state.username}
                        onChange={this.onLoginFormChange}
                        onFocusChanged={this.onEmailInputBlur}
                      />
                      {isEmailChecked ? (
                        <>
                          <CustomInput
                            isShow
                            type="password"
                            name="password"
                            value={this.state.password}
                            fieldName={t('general.password.title')}
                            placeholder={t('general.password.title')}
                            invalid={loginError}
                            validateMessage={
                              webAccessError ? (
                                <span>
                                  {t('log_in.product_not_included.part_one')}{' '}
                                  <a href="mailto:support@vormats.com\"> {t('log_in.product_not_included.support_team')} </a>{' '}
                                  {t('log_in.product_not_included.part_two')}{' '}
                                </span>
                              ) : (
                                <span>{t('log_in.wrong_credentials')} </span>
                              )
                            }
                            onChange={event => this.onLoginFormChange(event)}
                          />
                          <a href="#" onClick={this.onForgotClick} className={styles.contactLink}>
                            {t('log_in.forgot_my_password')}
                          </a>
                        </>
                      ) : (
                        <div className={styles.rememberMeContainer}>
                          <RadioButton
                            checked={isRememberChecked}
                            onChange={this.handleRememberChecked}
                            label={t('log_in.remember_me')}
                            id={'remember'}
                          />
                          <a href="#" onClick={this.onForgotClick} className={styles.contactLink}>
                            {t('log_in.forgot_my_password')}
                          </a>
                        </div>
                      )}

                      <CustomButton
                        onClick={this.onSubmit}
                        variant="primary"
                        label={t('general.continue')}
                        loading={this.state.loading}
                        disabled={
                          disabled ||
                          (!isEmailTouched && !isEmailValid) ||
                          (!this.state.username && !this.state.password) ||
                          !isEmailValid
                        }
                        loadingText={t('log_in.logging_in')}
                        className={styles.submitButton}
                        ariaLabel={t('general.continue')}
                        tabIndex={0}
                      />
                      {isEmailChecked && (
                        <div className={styles.loginFooter}>
                          <span>{t('log_in.dont_have_an_account')}</span>
                          <a href="https://vormats.com/demo/" title={t('log_in.contact_sale')} className={styles.contactLink}>
                            {t('log_in.contact_sale')}
                          </a>
                        </div>
                      )}
                    </form>
                  </div>
                </FormContainer>
              </div>
            </div>
          </MainContainer>
          <Footer />
        </>
      )
    );
  }
}

export default withTranslation()(withRouter(LoginPage));
